import React, { useEffect, useState } from "react";
import { DateInput } from "@/components/DateInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowRight, AlertTriangle } from "lucide-react";

export function MoveDialog({ pending, onConfirm, onCancel }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [pct, setPct] = useState(100);
  // procentul maxim ce poate fi mutat: de pe un proiect = alocarea curentă pe acel proiect;
  // din „Resurse disponibile" = procentul liber al persoanei.
  const maxPct = pending
    ? (pending.fromPid
        ? (pending.member.allocated_hours || 100)
        : (pending.member.free_pct != null ? pending.member.free_pct : 100))
    : 100;

  useEffect(() => {
    if (pending) {
      setStart(pending.member.start_date || "");
      setEnd(pending.member.end_date || "");
      const dflt = pending.fromPid
        ? (pending.member.allocated_hours || 100)
        : (pending.member.free_pct != null ? pending.member.free_pct : 100);
      setPct(dflt);
    }
  }, [pending]);

  if (!pending) return null;
  const { member, from, to, sourceWillBeEmpty } = pending;
  const pctNum = parseFloat(pct);
  const pctInvalid = !pctNum || pctNum <= 0 || pctNum > maxPct;
  const invalid = !start || !end || end < start || pctInvalid;

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md" data-testid="move-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl">Confirmă mutarea</DialogTitle>
          <DialogDescription>Alege procentul și perioada alocării pe proiectul destinație.</DialogDescription>
        </DialogHeader>
        <div className="mt-1 flex items-center gap-2 text-[13px] flex-wrap">
          <span className="font-semibold text-slate-800">{member.name}</span>
          <span className="pio-badge pio-badge-gray">{from ? (from.code || from.name) : "Disponibil"}</span>
          <ArrowRight className="h-4 w-4 text-slate-400" />
          <span className="pio-badge pio-badge-blue">{to.code || to.name}</span>
        </div>

        <label className="block mt-4">
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Procent alocat (%)</span>
          <div className="flex items-center gap-2 mt-1">
            <input type="number" step="5" min="1" max={maxPct} value={pct}
              onChange={(e) => setPct(e.target.value)} data-testid="move-pct"
              className="w-28 px-3 py-2 border border-neutral-200 focus:border-brand focus:outline-none text-sm font-mono" />
            <span className="text-[12px] text-slate-400">din max. {maxPct}% disponibil</span>
          </div>
          {pending.fromPid && pctNum > 0 && pctNum < maxPct && (
            <div className="text-[11px] text-slate-400 mt-1">
              Rămâne {maxPct - pctNum}% pe {from?.code || from?.name}.
            </div>
          )}
          {!pending.fromPid && pctNum > 0 && pctNum < maxPct && (
            <div className="text-[11px] text-slate-400 mt-1">
              Rămâne {maxPct - pctNum}% disponibil în „Resurse disponibile".
            </div>
          )}
          {pctInvalid && <div className="text-[12px] text-red-600 mt-1">Introdu un procent între 1 și {maxPct}.</div>}
        </label>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Data început</span>
            <DateInput value={start} onChange={(e) => setStart(e.target.value)} data-testid="move-start"
              className="mt-1 w-full px-3 py-2 border border-neutral-200 focus:border-brand focus:outline-none text-sm" />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Data sfârșit</span>
            <DateInput value={end} onChange={(e) => setEnd(e.target.value)} data-testid="move-end"
              className="mt-1 w-full px-3 py-2 border border-neutral-200 focus:border-brand focus:outline-none text-sm" />
          </label>
        </div>
        <div className="text-[11px] text-slate-400 mt-1">Alocarea se face cu perioadă — alege data de început și de sfârșit.</div>
        {start && end && end < start && <div className="text-[12px] text-red-600 mt-2">Data de sfârșit nu poate fi înaintea datei de început.</div>}
        {sourceWillBeEmpty && pctNum >= (member.allocated_hours || 0) && (
          <div data-testid="move-warning" className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-800">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>Atenție: proiectul <b>{from.name}</b> rămâne fără nicio resursă alocată după această mutare.</div>
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className="pio-btn-ghost" data-testid="move-cancel">Renunță</button>
          <button onClick={() => onConfirm({ start_date: start || null, end_date: end || null, pct: pctNum })} disabled={invalid}
            className="pio-btn disabled:opacity-50" data-testid="move-confirm">Confirmă mutarea</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
